import type { RouteLocationRaw } from 'vue-router'
import wcmatch from 'wildcard-match'

// Once https://github.com/nuxt/nuxt/issues/25891 will be fixed
// This middleware should be moved in the middleware directory of the auth layer
// For now it needs to stay here so it is executed after the external-url-handler global middleware

export default defineNuxtRouteMiddleware(async (to, from) => {
  if (!to.name) {
    // Prevent static asserts like images from being checked against auth
    // and triggering a redirection.
    return
  }

  const { data, refresh, signOut, status } = useAuth()
  const { setToken } = useAuthState()

  const accessToken = to.query.accessToken as string | undefined

  if (accessToken) {
    setToken(accessToken)
    await refresh()
    delete to.query.accessToken
  }

  const localePath = useLocalePath()

  const currentPage = to.name as string
  const currentLayout = (to.meta.layout as string | undefined) || 'default'

  const { auth } = useAppConfig()
  const isAuth = status.value === 'authenticated'

  // We ARE NOT auth and we want to access a protected area
  if (
    !isAuth
    && (auth.protectedLayouts.some(layout => wcmatch(layout)(currentLayout))
      || auth.protectedPages.some(page => wcmatch(page)(currentPage)))
  ) {
    const toUnauthenticatedPathLocation: RouteLocationRaw = {
      name: auth.redirectToIfUnauthenticated,
      query: { redirect: to.path },
    }

    if (to.path === '/') {
      delete toUnauthenticatedPathLocation.query?.redirect
    }

    return navigateTo(localePath(toUnauthenticatedPathLocation))
  }

  // We ARE auth and we want to access a guest area
  if (
    isAuth
    && (auth.guestsLayouts.some(layout =>
      wcmatch(layout)(to.meta.layout as string),
    )
    || auth.guestsPages.some(page => wcmatch(page)(currentPage)))
  ) {
    return navigateTo(localePath({ name: auth.redirectToAfterLogin(data as any) }))
  }

  // We ARE auth but for whatever reason we need to be signed out
  if (
    isAuth
    && !Object.values(auth.shouldRemainAuthFns).every((fn: any) =>
      fn(data.value, to, from),
    )
  ) {
    return signOut({
      callbackUrl: localePath({ name: auth.redirectToIfUnauthenticated }),
    })
  }
})
